import { Constructor, EmptyObject, IsEmptyObject, Primitive } from 'type-fest';

export type AbstractConstructor<Args extends unknown[], TInstance> = abstract new(...args: Args) => TInstance;
export type NonAbstract<T extends abstract new(...args: unknown[]) => unknown> = new(...args: ConstructorParameters<T>) => InstanceType<T>;

export function IsNumber(testValue: unknown): testValue is number {
  return typeof testValue === 'number';
}

export function generateUUID() { // Public Domain/MIT
  let d = new Date().getTime();// Timestamp
  let d2 = (performance && performance.now && (performance.now() * 1000)) || 0;// Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16;// random number between 0 and 16
    if (d > 0) { // Use timestamp until depleted
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    }
    else { // Use microseconds since page-load if supported
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

/**
 * Access an enum's number values by attempting to match it's keys with a string.
 * @param enumType The type of the given enum.
 * @param value A string that matches the name who's value is sought.
 * @returns value as number, or undefined if a match isn't made.
 */
export function GetValueByKeyForNumberEnum<T extends { [name: string]: unknown }>(enumType: T, value: string): number | undefined {
  return Object.entries(enumType).find(([key]) => key === value)?.[1] as number;
}

/**
 * Extract is basically the real intersection type for primitives. `&` is called the intersection operator but it's a lie.
 */
export type IsInUnion<Union, T> = Extract<Union, T> extends never ? false : true;

/**
 * Replaces all types of type `Replace` with type `Replacement` in given union `T`
 */
export type ReplaceInUnion<T, Replace, Replacement> = IsInUnion<T, Replace> extends true ? Replacement | Exclude<T, Replace> : T;

/**
 * P is a union of property names which may or may not exist on O.
 * O is an object.
 * @returns All property names in P that exist on object O.
 */
export type ExtractKeyof<P, O> = Extract<P, keyof O>;

/**
 * {} is a bad type. Make it better: https://github.com/sindresorhus/type-fest/blob/main/source/empty-object.d.ts
 */
export type EnforceEmpty<O> = IsEmptyObject<O> extends true ? EmptyObject : O;

/**
 * These are types we don't want to recursively map
 */
export type TerminalType = Primitive | ((...args: unknown[]) => unknown) | (abstract new (...args: unknown[]) => unknown);

export type ReadonlyShallow<T> = { [P in keyof T]: Readonly<T[P]> };

export type ReadonlyProps<T, P extends keyof T> = { readonly [K in P]: T[K] } & T;

/**
 * One way to do mixins is to make a function that receives a class, extends it at runtime,
 * and returns the resulting class (read: class, not class instance).
 *
 * This generic type grabs the extended class type since it is not otherwise accessible
 * from outside the function.
 */
export type MixinClass<MixinFunc extends (...args: unknown[]) => Constructor<unknown>> = InstanceType<ReturnType<MixinFunc>>;
